import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';

// Services
import { AssistantPanelService } from 'src/app/@core/services/ai/assistant-panel.service';

// Composants
import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';

// Modèles
import { Quotation } from 'src/app/@shared/models/sales/quotation/quotation.model';
import { SalesInvoice } from 'src/app/@shared/models/sales/invoice/sales-invoice.model';
import {
    LegalConsentDialogComponent,
    LegalConsentDialogData
} from 'src/app/legal/components/legal-consent-dialog/legal-consent-dialog.component';
import { LegalStatusEnum } from 'src/app/@shared/enums/legal-status.enum';
import { LegalDocumentTypeEnum } from 'src/app/@shared/enums/legal-document-type.enum';

@Component({
    selector: 'app-side-menu',
    imports: [
        CommonModule,
        RouterModule,
        BadgeModule,
        ButtonModule,
        TieredMenuModule,
        TooltipModule,
    ],
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss']
})
export class SideMenuComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @ViewChild('tieredMenu') tieredMenu!: TieredMenu;

    public menuItems: any[] = [];
    public helpMenuItems: any[] = [];
    public tieredMenuItems: MenuItem[] = [];

    private _assistantPanelService = inject(AssistantPanelService);
    private _lockedLinkClass = 'text-gray-300';
    private _showAccountingMenuItems = false;
    private _showBankMenuItems = false;
    private _showLegalMenuItems = false;
    private _showMiscellaneousMenuItems = false;
    private _showPurchasingMenuItems = false;
    private _showSalesMenuItems = false;

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/



    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    get showAccountingMenuItems(): boolean {
        let forceOpen = this.router.url.includes('accounting');
        return (forceOpen || this._showAccountingMenuItems) && this.appService.menuOpen;
    }

    get showBankMenuItems(): boolean {
        // Si la route contient 'banking'
        let forceOpen = this.router.url.includes('banking');
        return (forceOpen || this._showBankMenuItems) && this.appService.menuOpen;
    }

    get showLegalMenuItems(): boolean {
        // Si la route contient 'legal'
        let forceOpen = this.router.url.includes('legal');
        return (forceOpen || this._showLegalMenuItems) && this.appService.menuOpen;
    }

    get showMiscellaneousMenuItems(): boolean {
        // Si la route contient 'taxation', 'accounting/subsidies' ou 'human-resources/remunerations'
        let forceOpen = this.router.url.includes('taxation') || this.router.url.includes('accounting/subsidies') || this.router.url.includes('human-resources/remunerations');
        return (forceOpen || this._showMiscellaneousMenuItems) && this.appService.menuOpen;
    }

    get showPurchasingMenuItems(): boolean {
        let forceOpen = this.router.url.includes('purchasing');
        return (forceOpen || this._showPurchasingMenuItems) && this.appService.menuOpen;
    }

    get showSalesMenuItems(): boolean {
        let forceOpen = this.router.url.includes('sales');
        return (forceOpen || this._showSalesMenuItems) && this.appService.menuOpen;
    }

    /**
     * Compteur du badge « Actualités » : les annonces **mises en avant**, et non les
     * simples non lues. Une annonce expirée ou publiée avant l'inscription reste non lue
     * sans jamais devoir attirer l'œil — c'est la règle du contrat, et c'est ce qui
     * explique l'écart possible avec le filtre « Non lues » de l'écran d'historique.
     */
    get highlightedAnnouncementsCount(): number {
        return this.announcementService.highlighted.length;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    getTooltip(menuItem: any): string {
        return !this.appService.menuOpen && !menuItem.subItems ? menuItem.title : '';
    }

    onClickSectionTitle(section: string, event?: MouseEvent): void {
        if (section === 'assistant') {
            this._openAssistant(event);
            return;
        }
        // Si le menu est ouvert
        if (this.appService.menuOpen) {
            switch (section) {
                case 'accounting':
                    this._toggleAccountingMenuItems();
                    break;
                case 'bank':
                    this._toggleBankMenuItems();
                    break;
                case 'legal':
                    this._toggleLegalMenuItems();
                    break;
                case 'miscellaneous':
                    this._toggleMiscellaneousMenuItems();
                    break;
                case 'purchasing':
                    this._togglePurchasingMenuItems();
                    break;
                case 'sales':
                    this._toggleSalesMenuItems();
                    break;
            }
            this._buildMenu();
        }
    }

    onHoverSectionTitle(section: string, event: MouseEvent): void {
        // Si le menu est ouvert
        if (!this.appService.menuOpen) {
            // Ouverture du tiered menu avec les sous items
            let menuItem = this.menuItems.find(item => item.section === section);

            if (!menuItem) {
                menuItem = this.helpMenuItems.find(item => item.section === section);
            }

            // Même filtre par autorisation que le menu ouvert ; les entrées sans autorisation
            // déclarée (menu de l'aide) passent (#1204)
            const subItems: any[] = (menuItem.subItems ?? []).filter((subItem: any) =>
                !subItem.viewAuthorization || this.appService.user?.hasAuthorization(subItem.viewAuthorization)
            );

            if (subItems.length) {
                this.tieredMenuItems = subItems;
                let target = this._getHoverTarget(event.target as HTMLElement);
                this.tieredMenu.show(event);
                setTimeout(() => {
                    let tieredMenuTop = this._getTieredMenuTop(target);
                    (this.tieredMenu.container as HTMLElement).style.top = tieredMenuTop;
                }, 1);
            } else {
                this.tieredMenuItems = [];
                this.tieredMenu.hide();
            }

        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override subscribeToObservables(): void {
        this.subscriptions.add(
            this.appService.clientSelected$.subscribe(() => {
                setTimeout(() => {
                    this._buildMenu();
                }, 10);
            })
        );

        this.subscriptions.add(
            this.bankingSettingsService.updated$.subscribe(() => {
                setTimeout(() => {
                    this._buildMenu();
                }, 10);
            })
        );

        // Compteur d'actualités non lues porté par l'entrée de menu
        this.subscriptions.add(
            this.announcementService.announcements$.subscribe(() => {
                this._buildMenu();
            })
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _buildBankSubItems(): any[] {
        const bankingSettings = this.appService.selectedClient?.bankingSettings;
        const subItems: any[] = [];

        // Transactions bancaires - toujours visible
        subItems.push({
            link: this.appService.clientUrl('/banking/bank-account-lines'),
            title: 'Transactions bancaires',
            ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Banking) },
            locked: !this.appService.selectedClient?.hasFeature(this.Features.Banking),
            viewAuthorization: this.Authorizations.BankAccountLineRead,
        });

        // Relevés bancaires - si enableBankStatements
        if (bankingSettings?.enableBankStatements) {
            subItems.push({
                link: this.appService.clientUrl('/banking/statements'),
                title: 'Relevés bancaires',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Banking) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.Banking),
                action: this._importBankStatement.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Importer un relevé bancaire',
                createAuthorization: this.Authorizations.BankStatementCreate,
                viewAuthorization: this.Authorizations.BankStatementRead,
            });
        }

        // Encaissements carte - si enableCardReceipts
        if (bankingSettings?.enableCardReceipts) {
            subItems.push({
                link: this.appService.clientUrl('/banking/card-receipts'),
                title: 'Encaissements carte',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.CardReceipts) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.CardReceipts),
                action: this._createCardReceipt.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Saisir un encaissement carte',
                createAuthorization: this.Authorizations.CardReceiptCreate,
                viewAuthorization: this.Authorizations.CardReceiptRead
            });
        }

        // Débits différés - si enableCreditCardTransactions
        if (bankingSettings?.enableCreditCardTransactions) {
            subItems.push({
                link: this.appService.clientUrl('/banking/credit-card-transactions'),
                title: 'Débits différés',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.CreditCardTransactions) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.CreditCardTransactions),
                action: this._createCreditCardTransaction.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Saisir un débit différé',
                createAuthorization: this.Authorizations.CreditCardTransactionCreate,
                viewAuthorization: this.Authorizations.CreditCardTransactionRead,
            });
        }

        // Transactions en espèces - si enableCashTransactions
        if (bankingSettings?.enableCashTransactions) {
            subItems.push({
                link: this.appService.clientUrl('/banking/cash-transactions'),
                title: 'Transactions en espèces',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.CashManagement) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.CashManagement),
                action: this._createCashTransaction.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Saisir une transaction en espèces',
                createAuthorization: this.Authorizations.CashTransactionCreate,
                viewAuthorization: this.Authorizations.CashTransactionRead,
            });
        }

        // Chèques reçus et Remises de chèque - si enableCheque
        if (bankingSettings?.enableCheque) {
            subItems.push({
                link: this.appService.clientUrl('/banking/cheques'),
                title: 'Chèques reçus',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Cheques) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.Cheques),
                action: this._createCheque.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Saisir un chèque',
                createAuthorization: this.Authorizations.ChequeCreate,
                viewAuthorization: this.Authorizations.ChequeRead
            });
            subItems.push({
                link: this.appService.clientUrl('/banking/deposits'),
                title: 'Remises de chèques',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Cheques) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.Cheques),
                action: this._createDeposit.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Saisir une remise de chèque',
                createAuthorization: this.Authorizations.DepositCreate,
                viewAuthorization: this.Authorizations.DepositRead
            });
        }

        // Emprunts - si enableLoans
        if (bankingSettings?.enableLoans) {
            subItems.push({
                link: this.appService.clientUrl('/banking/loans'),
                title: 'Emprunts',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Loans) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.Loans),
                action: this._createLoan.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Saisir un emprunt',
                createAuthorization: this.Authorizations.LoanCreate,
                viewAuthorization: this.Authorizations.LoanRead
            });
        }

        return subItems;
    }

    private _buildMenu(): void {
        this.menuItems = [
            {
                section: 'dashboard',
                link: this.appService.clientUrl('/dashboard'),
                title: 'Dashboard',
                icon: 'fa-gauge',
                showSubItems: false,
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Dashboard) },
                showSection: true,
                locked: !this.appService.selectedClient?.hasFeature(this.Features.Dashboard)
            },
            {
                section: 'clients',
                link: this.appService.clientUrl('/settings/accounting-firm-clients'),
                title: 'Dossiers clients',
                icon: 'fa-folder-open',
                showSubItems: false,
                showSection: this.appService.selectedClient?.accountant
            },
            {
                section: 'bank',
                link: this.appService.clientUrl('/banking/bank-account-lines'),
                title: 'Banque',
                icon: this.appService.selectedClient?.hasFeature(this.Features.Banking) ? 'fa-building-columns' : 'fa-lock',
                showSubItems: this.showBankMenuItems,
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Banking) },
                showSection: true,
                locked: !this.appService.selectedClient?.hasFeature(this.Features.Banking),
                subItems: this._buildBankSubItems()
            },
            {
                section: 'sales',
                link: this.appService.clientUrl('/sales/invoices'),
                title: 'Ventes',
                icon: this.appService.selectedClient?.hasFeature(this.Features.Banking) ? 'fa-arrow-trend-up' : 'fa-lock',
                showSubItems: this.showSalesMenuItems,
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.SalesInvoices) },
                showSection: true,
                locked: !this.appService.selectedClient?.hasFeature(this.Features.SalesInvoices),
                subItems: this._buildSalesSubItems()
            },
            {
                section: 'purchasing',
                link: this.appService.clientUrl('/purchasing/invoices'),
                title: 'Achats',
                icon: this.appService.selectedClient?.hasFeature(this.Features.PurchaseInvoices) ? 'fa-arrow-trend-down' : 'fa-lock',
                showSubItems: this.showPurchasingMenuItems,
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.PurchaseInvoices) },
                showSection: true,
                locked: !this.appService.selectedClient?.hasFeature(this.Features.PurchaseInvoices),
                subItems: [
                    {
                        link: this.appService.clientUrl('/purchasing/invoices'),
                        title: 'Factures',
                        ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.PurchaseInvoices) },
                        locked: !this.appService.selectedClient?.hasFeature(this.Features.PurchaseInvoices),
                        viewAuthorization: this.Authorizations.PurchaseInvoiceRead,
                    },
                    {
                        link: this.appService.clientUrl('/purchasing/vendors'),
                        title: 'Fournisseurs',
                        ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.PurchaseInvoices) },
                        locked: !this.appService.selectedClient?.hasFeature(this.Features.PurchaseInvoices),
                        action: this._createVendor.bind(this),
                        actionIcon: PrimeIcons.PLUS,
                        actionTooltip: 'Ajouter un fournisseur',
                        createAuthorization: this.Authorizations.PartnerCreate,
                        viewAuthorization: this.Authorizations.PartnerRead
                    }
                ]
            },
            {
                section: 'accounting',
                link: this.appService.clientUrl('/accounting/journals'),
                title: 'Comptabilité',
                icon: this.appService.selectedClient?.hasFeature(this.Features.Accounting) ? 'fa-euro-sign' : 'fa-lock',
                showSubItems: this.showAccountingMenuItems,
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Accounting) },
                showSection: true,
                locked: !this.appService.selectedClient?.hasFeature(this.Features.Accounting),
                queryParams: { '$filter': "accountEntry/status eq 'posted'" },
                subItems: [
                    {
                        link: this.appService.clientUrl('/accounting/journals'),
                        title: 'Journaux',
                        ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Accounting) },
                        locked: !this.appService.selectedClient?.hasFeature(this.Features.Accounting),
                        viewAuthorization: this.Authorizations.AccountEntryRead,
                        queryParams: { '$filter': "accountEntry/status eq 'posted'" },
                    }
                ]
            },
            {
                section: 'miscellaneous',
                link: this.appService.clientUrl(this.appService.selectedClient?.isCaledonian ? '/taxation/tgc-declarations' : '/accounting/subsidies'),
                title: 'Opérations diverses',
                icon: 'fa-ellipsis',
                showSubItems: this.showMiscellaneousMenuItems,
                showSection: true,
                subItems: [
                    {
                        link: this.appService.clientUrl('/accounting/subsidies'),
                        title: 'Subventions',
                        ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Subsidies) },
                        locked: !this.appService.selectedClient?.hasFeature(this.Features.Subsidies),
                        action: this._createSubsidy.bind(this),
                        actionIcon: PrimeIcons.PLUS,
                        actionTooltip: 'Enregistrer une subvention',
                        createAuthorization: this.Authorizations.SubsidyCreate,
                        viewAuthorization: this.Authorizations.SubsidyRead,
                    },
                    {
                        link: this.appService.clientUrl('/human-resources/remunerations'),
                        title: 'Rémunérations',
                        ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Remunerations) },
                        locked: !this.appService.selectedClient?.hasFeature(this.Features.Remunerations),
                        action: this._createRemuneration.bind(this),
                        actionIcon: PrimeIcons.PLUS,
                        actionTooltip: 'Enregistrer une rémunération',
                        createAuthorization: this.Authorizations.RemunerationCreate,
                        viewAuthorization: this.Authorizations.RemunerationRead,
                    },
                    // Déclarations de TGC - réservées à la Nouvelle-Calédonie
                    ...(!!this.appService.selectedClient?.isCaledonian && !!this.appService.selectedClient?.taxationSettings?.vatSubject ? [{
                        link: this.appService.clientUrl('/taxation/tgc-declarations'),
                        title: `Déclarations de ${this.taxNameLabel}`,
                        ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.Tgc) },
                        locked: !this.appService.selectedClient?.hasFeature(this.Features.Tgc),
                        viewAuthorization: this.Authorizations.TGCDeclarationRead,
                    }] : []),
                ]
            }
        ];

        this.helpMenuItems = [
            {
                section: 'assistant',
                title: 'Assistant IA',
                icon: 'bi bi-ai-sparkles-solo',
                customIcon: true,
                showSubItems: false,
                showSection: !!this.appService.selectedClient?.hasFeature(this.Features.AiAgent),
                link: this.appService.clientUrl('/assistant'),
            },
            {
                section: 'documentation',
                title: 'Documentation',
                icon: 'fa-book-open',
                showSubItems: false,
                // Le manuel n'est pas atteignable sans dossier : il parle des
                // écrans d'un dossier, et le menu n'est pas affiché à ce moment-là.
                showSection: !!this.appService.selectedClient,
                link: this.appService.clientUrl('/documentation'),
            },
            {
                section: 'announcements',
                title: 'Actualités',
                icon: 'fa-bullhorn',
                showSubItems: false,
                showSection: true,
                link: '/announcements',
                badge: this.highlightedAnnouncementsCount,
            },
            {
                section: 'about',
                title: 'À propos',
                icon: 'fa-circle-info',
                showSubItems: false,
                showSection: true,
                link: '/about',
            },
            {
                section: 'legal',
                title: 'Légal',
                icon: 'fa-gavel',
                showSubItems: this.showLegalMenuItems,
                showSection: true,
                subItems: [
                    { link: '/legal/privacy', action: this._openPrivacyDialog.bind(this), title: 'Politique de confidentialité' },
                    { link: '/legal/legal', title: 'Mentions légales', newTab: true },
                    { link: '/legal/cookies', title: 'Cookies', newTab: true },
                    {
                        link: '/legal/cgv',
                        action: this._openCgvDialog.bind(this),
                        title: 'CGV',
                        showSection: !!this.appService.selectedClient
                    },
                    { link: '/legal/cgu', action: this._openCguDialog.bind(this), title: 'CGU' },
                ]
            }
        ];
    }

    private _buildSalesSubItems(): any[] {
        const isCaledonian = !!this.appService.selectedClient?.isCaledonian;
        const subItems: any[] = [];

        // Devis - création interne réservée à la Nouvelle-Calédonie
        if (isCaledonian) {
            subItems.push({
                link: this.appService.clientUrl('/sales/quotations'),
                title: 'Devis',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.SalesQuotations) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.SalesQuotations),
                action: this._createQuotation.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Créer un devis',
                createAuthorization: this.Authorizations.QuotationCreate,
                viewAuthorization: this.Authorizations.QuotationRead
            });
        }

        // Factures - toujours visibles (alimentées par l'OCR), création interne réservée à la Nouvelle-Calédonie
        const invoicesSubItem: any = {
            link: this.appService.clientUrl('/sales/invoices'),
            title: 'Factures',
            ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.SalesInvoices) },
            locked: !this.appService.selectedClient?.hasFeature(this.Features.SalesInvoices),
            viewAuthorization: this.Authorizations.SalesInvoiceRead
        };
        if (isCaledonian) {
            invoicesSubItem.action = this._createSalesInvoice.bind(this);
            invoicesSubItem.actionIcon = PrimeIcons.PLUS;
            invoicesSubItem.actionTooltip = 'Créer une facture de vente';
            invoicesSubItem.createAuthorization = this.Authorizations.SalesInvoiceCreate;
        }
        subItems.push(invoicesSubItem);

        // Clients - toujours visibles
        subItems.push({
            link: this.appService.clientUrl('/sales/clients'),
            title: 'Clients',
            ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.SalesInvoices) },
            locked: !this.appService.selectedClient?.hasFeature(this.Features.SalesInvoices),
            action: this._createClient.bind(this),
            actionIcon: PrimeIcons.PLUS,
            actionTooltip: 'Ajouter un client',
            createAuthorization: this.Authorizations.PartnerCreate,
            viewAuthorization: this.Authorizations.PartnerRead
        });

        // Articles - réservés à la Nouvelle-Calédonie
        if (isCaledonian) {
            subItems.push({
                link: this.appService.clientUrl('/settings/products'),
                title: 'Articles',
                ngClass: { [this._lockedLinkClass]: !this.appService.selectedClient?.hasFeature(this.Features.SalesInvoices) },
                locked: !this.appService.selectedClient?.hasFeature(this.Features.SalesInvoices),
                action: this._createProduct.bind(this),
                actionIcon: PrimeIcons.PLUS,
                actionTooltip: 'Ajouter un article',
                createAuthorization: this.Authorizations.ProductCreate,
                viewAuthorization: this.Authorizations.ProductRead
            });
        }

        return subItems;
    }

    private async _createCardReceipt(): Promise<void> {
        try {
            const { CardReceiptDialogComponent } = await import('src/app/banking/components/card-receipts/card-receipt-dialog/card-receipt-dialog.component');
            //  Ouverture de la fenêtre de création d'encaissement carte
            this.openDialog(CardReceiptDialogComponent, 'CARD_RECEIPT_DIALOG');
        } catch (error) {
            console.error('Erreur lors du chargement du composant CardReceiptDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private async _createCashTransaction(): Promise<void> {
        try {
            const { CashTransactionDialogComponent } = await import('src/app/banking/components/cash-transactions/cash-transaction-dialog/cash-transaction-dialog.component');
            //  Ouverture de la fenêtre de création de transaction en espèces
            this.openDialog(CashTransactionDialogComponent, 'CASH_TRANSACTION');
        } catch (error) {
            console.error('Erreur lors du chargement du composant CashTransactionDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private async _createCheque(): Promise<void> {
        try {
            const { ChequeDialogComponent } = await import('src/app/banking/components/cheques/cheque-dialog/cheque-dialog.component');
            //  Ouverture de la fenêtre de création de chèque
            this.openDialog(ChequeDialogComponent, 'CHEQUE_DIALOG');
        } catch (error) {
            console.error('Erreur lors du chargement du composant ChequeDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private async _createClient(): Promise<void> {
        try {
            const { PartnerDialogComponent } = await import('src/app/partners/components/partners/partner-dialog/partner-dialog.component');
            //  Ouverture de la fenêtre de création de client
            this.openDialog(PartnerDialogComponent, 'PARTNER', { mode: 'client' });
        } catch (error) {
            console.error('Erreur lors du chargement du composant PartnerDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private async _createCreditCardTransaction(): Promise<void> {
        try {
            const { CreditCardTransactionDialogComponent } = await import('src/app/banking/components/credit-card-transactions/credit-card-transaction-dialog/credit-card-transaction-dialog.component');
            //  Ouverture de la fenêtre de création de débit différé
            this.openDialog(CreditCardTransactionDialogComponent, 'CREDIT_CARD_TRANSACTION_DIALOG');
        } catch (error) {
            console.error('Erreur lors du chargement du composant CreditCardTransactionDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private async _createDeposit(): Promise<void> {
        try {
            const { DepositDialogComponent } = await import('src/app/banking/components/deposits/deposit-dialog/deposit-dialog.component');
            //  Ouverture de la fenêtre de création de client
            this.openDialog(DepositDialogComponent, 'DEPOSIT_DIALOG');
        } catch (error) {
            console.error('Erreur lors du chargement du composant DepositDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private async _createLoan(): Promise<void> {
        try {
            const { LoanCreationWizardComponent } = await import('src/app/banking/components/loans/loan-creation-wizard/loan-creation-wizard.component');
            //  Ouverture du wizard de création d'un emprunt
            this.openDialog(LoanCreationWizardComponent, 'LOAN_WIZARD');
        } catch (error) {
            console.error('Erreur lors du chargement du composant LoanCreationWizardComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private async _createProduct(): Promise<void> {
        try {
            const { ProductDialogComponent } = await import('src/app/products/components/product-dialog/product-dialog.component');
            this.openDialog(ProductDialogComponent, 'PRODUCT');
        } catch (error) {
            console.error('Erreur lors du chargement du composant ProductDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private _createQuotation(): void {
        //  Création du devis et redirection vers la page de modification
        let quotation = Quotation.init();
        this.quotationService.save$(quotation).subscribe(
            (quotation: Quotation) => this.router.navigate([this.appService.clientUrl('/sales/quotation'), quotation.id])
        );
    }

    private _createSalesInvoice(): void {
        //  Création de la facture de vente et redirection vers la page de modification
        let salesInvoice = SalesInvoice.init();
        this.salesInvoiceService.save$(salesInvoice).subscribe(
            (salesInvoice: SalesInvoice) => this.router.navigate([this.appService.clientUrl('/sales/invoice'), salesInvoice.id])
        );
    }

    private async _createRemuneration(): Promise<void> {
        try {
            const { RemunerationDialogComponent } = await import('src/app/human-resources/components/remunerations/remuneration-dialog/remuneration-dialog.component');
            this.openDialog(RemunerationDialogComponent, 'REMUNERATION_DIALOG');
        } catch (error) {
            console.error('Erreur lors du chargement du composant RemunerationDialogComponent', error);
        }
    }

    private async _createSubsidy(): Promise<void> {
        try {
            const { SubsidyDialogComponent } = await import('src/app/accounting/components/subsidies/subsidy-dialog/subsidy-dialog.component');
            //  Ouverture de la fenêtre de création de subvention
            this.openDialog(SubsidyDialogComponent, 'SUBSIDY_DIALOG');
        } catch (error) {
            console.error('Erreur lors du chargement du composant SubsidyDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private async _createVendor(): Promise<void> {
        try {
            const { PartnerDialogComponent } = await import('src/app/partners/components/partners/partner-dialog/partner-dialog.component');
            //  Ouverture de la fenêtre de création de client
            this.openDialog(PartnerDialogComponent, 'PARTNER', { mode: 'vendor' });
        } catch (error) {
            console.error('Erreur lors du chargement du composant PartnerDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    /**
     * Récupéartion de l'élément survolé pour afficher le menu contextuel.#item
     *
     * On remonte jusqu'à l'élément <a> parent si l'élément survolé est un enfant
     * @param target L'élément survolé
     * @returns Le lien parent
     */
    private _getHoverTarget(target: HTMLElement): HTMLElement {
        while (target.tagName !== 'DIV') {
            target = target.parentElement as HTMLElement;
        }
        return target;
    }

    private _getTieredMenuTop(target: HTMLElement): string {
        // Si le menu ouvert dépasse le bas de la fenêtre
        if (target.offsetTop + (this.tieredMenu.container as HTMLElement).offsetHeight > window.innerHeight) {
            return `${target.offsetTop - (this.tieredMenu.container as HTMLElement).offsetHeight + target.offsetHeight}px`;
        } else {
            return `${target.offsetTop}px`;
        }
    }

    private async _importBankStatement(): Promise<void> {
        try {
            const { BankStatementImportDialogComponent } = await import('src/app/banking/components/statements/bank-statement-import-dialog/bank-statement-import-dialog.component');
            //  Ouverture de la fenêtre d'import d'un relevé bancaire
            this.openDialog(BankStatementImportDialogComponent, 'BANK_STATEMENT_IMPORT');
        } catch (error) {
            console.error('Erreur lors du chargement du composant BankStatementImportDialogComponent', error);
            // Gérer l'erreur (afficher un message à l'utilisateur, etc.)
        }
    }

    private _toggleAccountingMenuItems(): void {
        this._showAccountingMenuItems = !this._showAccountingMenuItems;
    }

    private _toggleBankMenuItems(): void {
        this._showBankMenuItems = !this._showBankMenuItems;
    }

    /**
     * L'assistant s'ouvre en pleine page, sur l'accueil : la conversation laissée en cours est
     * effacée, contrairement au panneau où elle reste jusqu'à « Nouvelle conversation ». « Réduire »
     * ramène à l'écran qu'on quitte. Un clic qui ouvre un nouvel onglet (cmd, ctrl, maj, clic molette)
     * ne touche à rien : l'onglet d'origine garde son écran et sa conversation.
     */
    private _openAssistant(event?: MouseEvent): void {
        if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) {
            return;
        }
        this._assistantPanelService.rememberReturnUrl();
        this.aiAssistantService.newConversation();
    }

    private _openCguDialog(): void {
        const user = this.appService.user;
        const mode = (user?.cguStatus === LegalStatusEnum.OK) ? 'info'
            : (user?.cguStatus === LegalStatusEnum.OBSOLETE) ? 'optional'
                : 'blocking';
        const data: LegalConsentDialogData = { documentType: LegalDocumentTypeEnum.CGU, mode };
        this.openDialog(LegalConsentDialogComponent, 'LEGAL_CONSENT_DIALOG', data);
    }

    private _openPrivacyDialog(): void {
        const user = this.appService.user;
        const mode = (user?.privacyStatus === LegalStatusEnum.OK) ? 'info'
            : (user?.privacyStatus === LegalStatusEnum.OBSOLETE) ? 'optional'
                : 'blocking';
        const data: LegalConsentDialogData = { documentType: LegalDocumentTypeEnum.PRIVACY, mode };
        this.openDialog(LegalConsentDialogComponent, 'LEGAL_CONSENT_DIALOG', data);
    }

    private _openCgvDialog(): void {
        const client = this.appService.selectedClient;
        if (!client) return;
        const canAccept = this.appService.user?.hasAuthorization(this.Authorizations.LegalConsentCreate) ?? false;
        const mode = !canAccept ? 'no-access'
            : (client.cgvStatus === LegalStatusEnum.OK) ? 'info'
                : (client.cgvStatus === LegalStatusEnum.OBSOLETE) ? 'optional'
                    : 'blocking';
        const data: LegalConsentDialogData = { documentType: LegalDocumentTypeEnum.CGV, mode, clientId: client.id };
        this.openDialog(LegalConsentDialogComponent, 'LEGAL_CONSENT_DIALOG', data);
    }

    private _toggleLegalMenuItems(): void {
        this._showLegalMenuItems = !this._showLegalMenuItems;
    }

    private _toggleMiscellaneousMenuItems(): void {
        this._showMiscellaneousMenuItems = !this._showMiscellaneousMenuItems;
    }

    private _togglePurchasingMenuItems(): void {
        this._showPurchasingMenuItems = !this._showPurchasingMenuItems;
    }

    private _toggleSalesMenuItems(): void {
        this._showSalesMenuItems = !this._showSalesMenuItems;
    }
}
